class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    // Initialize dynamic viewport height variable for mobile browsers
    this.initViewportHeightVar();
    
    // Debug: Log if predictive search container is found
    if (!this.predictiveSearchResults) {
      console.warn('Predictive search results container not found');
    }
  }

  connectedCallback() {
    // Call parent's connectedCallback to initialize reset button
    if (super.connectedCallback) {
      super.connectedCallback();
    }
  }
  
  disconnectedCallback() {
    // Clean up any lingering classes
    document.body.classList.remove('predictive-search-navigating');
    // Remove visualViewport listeners if added
    if (this._vvHandlers) {
      const vv = window.visualViewport;
      if (vv) {
        vv.removeEventListener('resize', this._vvHandlers.onChange);
        vv.removeEventListener('scroll', this._vvHandlers.onChange);
      }
      window.removeEventListener('orientationchange', this._vvHandlers.onOrientation);
      this._vvHandlers = null;
    }
    // Remove dropdown positioning listeners
    if (this._posHandlers) {
      window.removeEventListener('scroll', this._posHandlers.onScroll, true);
      window.removeEventListener('resize', this._posHandlers.onResize);
      const vv = window.visualViewport;
      if (vv) vv.removeEventListener('resize', this._posHandlers.onVVResize);
      this._posHandlers = null;
    }
  }
  
  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // Prevent loading screen on search result clicks
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (link && this.contains(link)) {
        // Add class to body to prevent loading screen
        document.body.classList.add('predictive-search-navigating');
        
        // Remove class after navigation - increased timeout
        setTimeout(() => {
          document.body.classList.remove('predictive-search-navigating');
        }, 500);
        
        // Also remove on page unload
        window.addEventListener('beforeunload', () => {
          document.body.classList.remove('predictive-search-navigating');
        }, { once: true });
      }
    });
    
    // Handle mobile keyboard adjustments
    this.setupMobileKeyboardHandler();
    
    // Handle "Search for" button click
    this.setupSearchForButton();
  }
  
  setupSearchForButton() {
    // Add click handler for "Search for" button
    this.addEventListener('click', (event) => {
      const searchForButton = event.target.closest('.predictive-search__item--term');
      if (searchForButton && this.input?.form) {
        event.preventDefault();
        // Submit the form to perform full search
        this.input.form.submit();
      }
    });
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }
  
  setupMobileKeyboardHandler() {
    // Only apply to mobile
    if (window.innerWidth > 749) return;
    
    // Simple focus handler to ensure search results remain accessible
    if (this.input) {
      this.input.addEventListener('focus', () => {
        // Add a class when keyboard is likely open
        document.body.classList.add('search-keyboard-open');
      });
      
      this.input.addEventListener('blur', () => {
        // Remove class when keyboard closes
        setTimeout(() => {
          document.body.classList.remove('search-keyboard-open');
        }, 100);
      });
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || (newSearchTerm && !newSearchTerm.startsWith(this.searchTerm))) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (!this.input) return;
      const related = event.relatedTarget;
      // If focus moved to an element inside the predictive-search component, do nothing
      if (related && this.contains(related)) return;

      // If there is no related target, close only if active element is outside
      if (!related && !this.contains(document.activeElement)) {
        this.close();
        return;
      }

      // Otherwise, focus moved outside the component: close the dropdown
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 100);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const regex = new RegExp(this.escapeRegExp(previousTerm), 'g');
      const matches = currentButtonText.match(regex);
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      // Check if it's the "Search for" button
      if (selectedOption.classList.contains('predictive-search__item--term')) {
        // Submit the form for full search
        if (this.input?.form) {
          this.input.form.submit();
        }
      } else {
        // Regular link click
        selectedOption.click();
      }
    }
  }

  getSearchResults(searchTerm) {
    if (!searchTerm) return; 
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[limit]=10&resources[options][unavailable_products]=last&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    // Position dropdown directly under input on mobile to avoid visual gap
    this.positionDropdown();
    this.bindPositioningHandlers();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    
    // Reset styles properly
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = '';
      this.predictiveSearchResults.style.opacity = '';
    }
    // Clear dropdown top CSS variable
    document.documentElement.style.removeProperty('--search-dropdown-top');
    // Unbind positioning handlers
    if (this._posHandlers) {
      window.removeEventListener('scroll', this._posHandlers.onScroll, true);
      window.removeEventListener('resize', this._posHandlers.onResize);
      const vv = window.visualViewport;
      if (vv) vv.removeEventListener('resize', this._posHandlers.onVVResize);
      this._posHandlers = null;
    }
  }
  
  // Initialize and maintain a CSS variable for the real visual viewport height on mobile
  initViewportHeightVar() {
    const update = () => {
      try {
        const vv = window.visualViewport;
        const height = vv ? vv.height : window.innerHeight;
        // Use pixels to avoid sub-pixel issues; CSS consumes this via var(--viewport-height)
        document.documentElement.style.setProperty('--viewport-height', `${Math.round(height)}px`);
      } catch (e) {
        // No-op
      }
    };

    // Store handlers for cleanup
    this._vvHandlers = {
      onChange: () => update(),
      onOrientation: () => setTimeout(update, 100),
    };

    // Initial set
    update();

    // Listen to changes when available (mobile keyboards, pinch/zoom, etc.)
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', this._vvHandlers.onChange);
      vv.addEventListener('scroll', this._vvHandlers.onChange);
    }
    window.addEventListener('orientationchange', this._vvHandlers.onOrientation);
  }

  // Calculate and set the dropdown top to align with the bottom of the input on mobile
  positionDropdown() {
    try {
      if (window.innerWidth > 749) return; // mobile only
      if (!this.input) return;
      const rect = this.input.getBoundingClientRect();
      const topPx = Math.round(rect.bottom);
      document.documentElement.style.setProperty('--search-dropdown-top', `${topPx}px`);
    } catch (e) {
      // No-op
    }
  }

  bindPositioningHandlers() {
    if (this._posHandlers) return;
    this._posHandlers = {
      onScroll: () => {
        if (this.isOpen) this.positionDropdown();
      },
      onResize: () => {
        if (this.isOpen) this.positionDropdown();
      },
      onVVResize: () => {
        if (this.isOpen) this.positionDropdown();
      }
    };
    window.addEventListener('scroll', this._posHandlers.onScroll, true);
    window.addEventListener('resize', this._posHandlers.onResize);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', this._posHandlers.onVVResize);
  }
}

customElements.define('predictive-search', PredictiveSearch);

